/**
 * NEW_UX editor shell primitives (Layout A: icon rail + top bar + inspector + preview canvas).
 * Pure presentational styled-components + thin composers. No editor logic lives here.
 */
import styled from '@emotion/styled';
import React from 'react';

import { tokens } from './tokens';

const { color, layout, radius, font } = tokens;

/** Root: fills the hosting dialog; icon rail + right column. */
export const EditorShell = styled('div')`
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
  min-width: 0;
  min-height: 0;
  background: ${color.white};
  color: ${color.textPrimary};
  font-weight: ${font.weightScale.regular};
`;

export const RightColumn = styled('div')`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
`;

/* ------------------------------------------------------------------ icon rail */

export const SectionRailContainer = styled('div')`
  width: ${layout.railWidth}px;
  flex: 0 0 ${layout.railWidth}px;
  border-right: 1px solid ${color.borderSubtle};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 14px 0;
  gap: ${tokens.space(2)};
`;

const RailBtn = styled('button')<{ active?: boolean }>`
  position: relative;
  width: ${layout.railBtn}px;
  height: ${layout.railBtn}px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: ${radius.md}px;
  cursor: pointer;
  background: ${(p) => (p.active ? color.blue100 : 'transparent')};
  color: ${(p) => (p.active ? color.blue600 : color.n500)};
  &:hover {
    background: ${(p) => (p.active ? color.blue100 : color.n100)};
    color: ${(p) => (p.active ? color.blue600 : color.n800)};
  }
  &::before {
    content: '';
    position: absolute;
    left: -14px;
    top: 9px;
    bottom: 9px;
    width: 3px;
    border-radius: 0 3px 3px 0;
    background: ${(p) => (p.active ? color.blue600 : 'transparent')};
  }
`;

export const RailButton: React.FC<{
  active?: boolean;
  title: string;
  onClick: () => void;
  children: React.ReactNode; // icon
}> = ({ active, title, onClick, children }) => (
  <RailBtn active={active} title={title} aria-label={title} onClick={onClick}>
    {children}
  </RailBtn>
);

/* --------------------------------------------------------------------- top bar */

export const TopBar = styled('div')`
  height: ${layout.topBarHeight}px;
  flex: 0 0 ${layout.topBarHeight}px;
  display: flex;
  align-items: center;
  gap: ${tokens.space(2)};
  padding: 0 ${tokens.space(3)};
  border-bottom: 1px solid ${color.borderSubtle};
`;

export const TopBarCrumb = styled('div')`
  font-size: ${font.variant.caption.fontSize}px;
  line-height: ${font.variant.caption.lineHeight}px;
  font-weight: ${font.variant.caption.fontWeight};
  color: ${color.grey500};
`;

export const TopBarTitle = styled('div')`
  font-size: ${font.variant.h5.fontSize}px;
  line-height: ${font.variant.h5.lineHeight}px;
  font-weight: ${font.variant.h5.fontWeight};
  color: ${color.black};
  display: flex;
  align-items: center;
  gap: ${tokens.space(2)};
`;

export const TopBarSpacer = styled('div')`
  flex: 1 1 auto;
`;

export const TopBarRight = styled('div')`
  display: flex;
  align-items: center;
  gap: ${tokens.space(2)};
`;

/* -------------------------------------------------------------- body / panels */

export const EditorBody = styled('div')`
  display: flex;
  flex-direction: row;
  flex: 1 1 auto;
  min-height: 0;
`;

export const InspectorPanel = styled('div')`
  width: ${layout.inspectorWidth}px;
  flex: 0 0 ${layout.inspectorWidth}px;
  border-right: 1px solid ${color.borderSubtle};
  overflow-y: auto;
  padding: ${tokens.space(5)};
`;

const PanelHeadRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 24px;
  & svg {
    width: 18px;
    height: 18px;
    color: ${color.black};
  }
`;

const PanelHeadTitle = styled('div')`
  font-size: ${font.variant.h5.fontSize}px;
  line-height: ${font.variant.h5.lineHeight}px;
  font-weight: ${font.variant.h5.fontWeight};
  color: ${color.black};
  white-space: nowrap;
`;

const PanelHint = styled('div')`
  font-size: ${font.variant.caption.fontSize}px;
  line-height: ${font.variant.caption.lineHeight}px;
  font-weight: ${font.variant.caption.fontWeight};
  color: ${color.grey500};
  margin: -8px 0 14px;
`;

/** Inspector panel header: icon + title + descriptive hint. */
export const PanelHeader: React.FC<{
  icon?: React.ReactNode;
  title: string;
  hint?: string;
}> = ({ icon, title, hint }) => (
  <>
    <PanelHeadRow>
      {icon}
      <PanelHeadTitle>{title}</PanelHeadTitle>
    </PanelHeadRow>
    {hint && <PanelHint>{hint}</PanelHint>}
  </>
);

/* ------------------------------------------------------------- preview canvas */

const PreviewCanvasDiv = styled('div')`
  flex: 1 1 auto;
  min-width: 0;
  overflow-y: auto;
  background: ${color.canvas};
  padding: ${tokens.space(6)};
  display: flex;
  justify-content: center;
`;

/**
 * The scrollable canvas behind a shell's block preview. `onBackgroundClick` is
 * a required prop rather than a plain `onClick` you could forget to pass —
 * every newUx editor/viewer shell is expected to deselect its current block
 * when the empty canvas background is clicked, and that expectation used to
 * live only as an unenforced convention, which is exactly why it went missing
 * from half of these shells.
 */
export const PreviewCanvas: React.FC<{
  onBackgroundClick: () => void;
  children: React.ReactNode;
}> = ({ onBackgroundClick, children }) => (
  <PreviewCanvasDiv onClick={onBackgroundClick}>{children}</PreviewCanvasDiv>
);

/** Centered white "email/task" sheet with the brand-green top bar. */
export const PreviewSheet = styled('div')`
  width: ${layout.sheetWidth}px;
  max-width: 100%;
  align-self: flex-start;
  background: ${color.white};
  border-radius: ${radius.lg}px;
  box-shadow: ${tokens.shadow.lg};
  overflow: hidden;
  &::before {
    content: '';
    display: block;
    height: 6px;
    background: ${color.green};
  }
`;
